import { z } from "zod";
import { chaveValida, cnpjValido, normalizarChave, normalizarCnpj } from "@fiscoai/nfe/identificadores";
//#region src/acesso.ts
/**
* Catálogo de permissões — fonte única para server (autorização) e web (o que exibir).
* Duas camadas (docs/arquitetura/controle-de-acesso.md, ADR 0008):
*  - `plataforma.*`: administração da instalação, vêm do perfil de plataforma do usuário;
*  - demais: valem dentro de uma empresa, vêm do perfil do vínculo usuário↔empresa.
* Append-only: renomear ou remover exige migration dos perfis gravados.
*/
const PERMISSOES_PLATAFORMA = [
	"plataforma.usuarios.gerenciar",
	"plataforma.perfis.gerenciar",
	"plataforma.empresas.gerenciar",
	"plataforma.empresas.acessar_todas",
	"plataforma.auditoria.ler",
	"plataforma.usuarios.ler"
];
const PERMISSOES_EMPRESA = [
	"empresa.cadastro.ler",
	"empresa.cadastro.editar",
	"empresa.certificado.gerenciar",
	"empresa.membros.gerenciar",
	"documentos.ler",
	"documentos.importar",
	"documentos.baixar",
	"documentos.classificar",
	"documentos.remover",
	"documentos.exportar",
	"chaves.gerenciar",
	"conciliacao.ler",
	"conciliacao.vincular",
	"validacoes.solicitar",
	"validacoes.responder",
	"relatorios.ler",
	"integracoes.gerenciar",
	"ia.analisar",
	"empresa.captura.gerenciar",
	"documentos.captura.executar"
];
const permissaoPlataformaSchema = z.enum(PERMISSOES_PLATAFORMA);
const permissaoEmpresaSchema = z.enum(PERMISSOES_EMPRESA);
const permissaoSchema = z.enum([...PERMISSOES_PLATAFORMA, ...PERMISSOES_EMPRESA]);
/** Type guard: descarta chaves gravadas que não existem (mais) no catálogo. */
function ehPermissao(valor) {
	return permissaoSchema.safeParse(valor).success;
}
function ehPermissaoDePlataforma(valor) {
	return valor.startsWith("plataforma.");
}
/** Em que camada um perfil atua — um perfil nunca mistura as duas. */
const escopoPerfilSchema = z.enum(["PLATAFORMA", "EMPRESA"]);
/** Perfis de sistema: criados pelo seed, não podem ser apagados nem renomeados. */
const CHAVES_PERFIS_SISTEMA = [
	"admin",
	"fiscal_lider",
	"fiscal",
	"contabil",
	"comprador_validador",
	"auditor",
	"gestao_fiscal"
];
const chavePerfilSchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, "Use letras minúsculas, números e _ (2 a 40 caracteres)");
/** Permissões efetivas de quem chama: as de plataforma e, por empresa, as do vínculo. */
const contextoAcessoSchema = z.object({
	usuarioId: z.uuid(),
	permissoesPlataforma: z.array(permissaoPlataformaSchema),
	/** Empresa → permissões nela. Com `acessar_todas`, inclui todas as empresas ativas. */
	empresas: z.record(z.uuid(), z.array(permissaoEmpresaSchema))
});
//#endregion
//#region src/auditoria.ts
/** Trilha append-only das ações relevantes (`plataforma.auditoria.ler`). */
const eventoAuditoriaSchema = z.object({
	id: z.uuid(),
	acao: z.string(),
	alvoTipo: z.string(),
	alvoId: z.string().nullable(),
	empresaId: z.uuid().nullable(),
	ator: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	detalhes: z.record(z.string(), z.unknown()),
	ip: z.string().nullable(),
	ocorridoEm: z.iso.datetime()
});
const listarAuditoriaQuerySchema = z.object({
	acao: z.string().max(80).optional(),
	empresaId: z.uuid().optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
});
//#endregion
//#region src/auth.ts
/** Senha: mínimo de 12 caracteres (NIST SP 800-63B), máximo que o argon2 trata com folga. */
const senhaSchema = z.string().min(12, "A senha deve ter pelo menos 12 caracteres").max(256, "A senha deve ter no máximo 256 caracteres");
/** Normaliza (espaços, maiúsculas) ANTES de validar — e-mail colado com espaço é comum. */
const emailSchema = z.string().trim().toLowerCase().pipe(z.email("E-mail inválido").max(254));
/** `POST /api/v1/auth/login` — só valida formato; a regra de tamanho da senha não se aplica aqui. */
const loginRequestSchema = z.object({
	email: emailSchema,
	senha: z.string().min(1, "Informe a senha").max(256)
});
/**
* Resposta de login e de `POST /api/v1/auth/refresh`. O refresh token NUNCA vem no corpo: vai em
* cookie httpOnly. O access token fica só em memória no cliente (ADR 0006).
*/
const sessaoSchema = z.object({
	accessToken: z.string(),
	tokenType: z.literal("Bearer"),
	/** Segundos até o access token expirar. */
	expiresIn: z.number().int().positive(),
	/** Fim absoluto da sessão (ISO 8601): depois disso, só com novo login. */
	sessaoExpiraEm: z.iso.datetime()
});
/** Empresa acessível pelo usuário, com o perfil que ele exerce nela. */
const vinculoResumoSchema = z.object({
	empresa: z.object({
		id: z.uuid(),
		cnpj: z.string(),
		razaoSocial: z.string(),
		nomeFantasia: z.string().nullable()
	}),
	perfil: z.object({
		chave: z.string(),
		nome: z.string()
	}).nullable(),
	permissoes: z.array(permissaoEmpresaSchema)
});
/** `GET /api/v1/auth/me` — quem está logado e o que pode fazer. */
const usuarioAtualSchema = z.object({
	id: z.uuid(),
	nome: z.string(),
	email: z.email(),
	perfilPlataforma: z.object({
		chave: z.string(),
		nome: z.string()
	}).nullable(),
	permissoesPlataforma: z.array(permissaoPlataformaSchema),
	/** Empresas com vínculo; com `plataforma.empresas.acessar_todas`, todas as ativas. */
	vinculos: z.array(vinculoResumoSchema),
	/** Senha definida por outra pessoa: até trocar, só a tela de troca de senha funciona. */
	trocaSenhaPendente: z.boolean()
});
/**
* `POST /api/v1/auth/senha` — a pessoa troca a própria senha, confirmando a atual. Devolve uma
* sessão nova (as outras são encerradas).
*/
const trocarSenhaRequestSchema = z.object({
	senhaAtual: z.string().min(1, "Informe a senha atual").max(256),
	novaSenha: senhaSchema
}).refine((t) => t.novaSenha !== t.senhaAtual, {
	message: "A nova senha precisa ser diferente da atual",
	path: ["novaSenha"]
});
/**
* Final do `type` (RFC 9457) do 403 de troca de senha pendente: o web reconhece por ele e leva a
* pessoa à tela de troca.
*/
const PROBLEMA_TROCA_SENHA = "password-change-required";
//#endregion
//#region src/comum.ts
/** Identificador opaco (UUID v7 gerado pelo server). */
const idSchema = z.uuid();
/** Corpo de erro padrão — RFC 9457. `errors` só em falha de validação, um item por campo. */
const problemDetailsSchema = z.object({
	type: z.string(),
	title: z.string(),
	status: z.number().int(),
	detail: z.string().optional(),
	instance: z.string().optional(),
	requestId: z.string().optional(),
	errors: z.array(z.object({
		path: z.string(),
		message: z.string()
	})).optional(),
	/** Captura na SEFAZ (409): quando a SEFAZ volta a aceitar consulta deste CNPJ. */
	liberadaEm: z.iso.datetime().optional()
});
/** Paginação por cursor: `nextCursor` nulo indica o fim. */
const cursorQuerySchema = z.object({
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
});
function cursorPageSchema(item) {
	return z.object({
		items: z.array(item),
		nextCursor: z.string().nullable()
	});
}
/** Quantos itens por página a tabela oferece. */
const OPCOES_POR_PAGINA = [
	10,
	25,
	50,
	100
];
const POR_PAGINA_PADRAO = 25;
/**
* Query comum das listas em tabela: página (1…), itens por página e a direção da ordem. O campo
* de ordenação (`ordenarPor`) é próprio de cada lista — só colunas com índice.
*/
const paginacaoQuerySchema = z.object({
	pagina: z.coerce.number().int().min(1).max(1e5).default(1),
	porPagina: z.coerce.number().int().refine((n) => OPCOES_POR_PAGINA.includes(n), { message: `Itens por página: ${OPCOES_POR_PAGINA.join(", ")}` }).default(25),
	ordem: z.enum(["asc", "desc"]).default("desc")
});
/** Uma página de resultados com o total do filtro (para "1–25 de 231" e os números de página). */
function paginaNumeradaSchema(item) {
	return z.object({
		items: z.array(item),
		total: z.number().int().nonnegative(),
		pagina: z.number().int().min(1),
		porPagina: z.number().int().min(1)
	});
}
/** Valor fiscal como string decimal ("1234.50"): nunca `number` (ADR 0009). */
const decimalSchema = z.string().regex(/^-?\d+(\.\d+)?$/, "Valor decimal inválido");
/** CNPJ numérico ou alfanumérico; aceita máscara e devolve normalizado (14 posições, maiúsculas). */
const cnpjSchema = z.string().trim().transform(normalizarCnpj).refine(cnpjValido, "CNPJ inválido");
/** Chave de acesso de 44 posições; aceita espaços e devolve normalizada. */
const chaveAcessoSchema = z.string().trim().transform(normalizarChave).refine(chaveValida, "Chave de acesso inválida");
const ufSchema = z.enum([
	"AC",
	"AL",
	"AM",
	"AP",
	"BA",
	"CE",
	"DF",
	"ES",
	"GO",
	"MA",
	"MG",
	"MS",
	"MT",
	"PA",
	"PB",
	"PE",
	"PI",
	"PR",
	"RJ",
	"RN",
	"RO",
	"RR",
	"RS",
	"SC",
	"SE",
	"SP",
	"TO",
	"EX"
]);
/** Data civil "AAAA-MM-DD" (vigências, vencimentos). */
const dataSchema = z.iso.date();
/** Lista de empresas na query string: `empresaIds=a,b` ou repetido; vazio = todas as permitidas. */
const empresaIdsQuerySchema = z.union([z.string(), z.array(z.string())]).optional().transform((valor) => {
	if (valor === void 0) return [];
	return (Array.isArray(valor) ? valor : valor.split(",")).map((id) => id.trim()).filter(Boolean);
}).pipe(z.array(z.uuid()).max(200));
//#endregion
//#region src/captura.ts
/**
* Captura automática de NF-e na SEFAZ (spec 0006, ADR 0025): agenda por empresa e estado.
* Rotas em `/api/v1/empresas/:empresaId/captura…`.
*/
/**
* Espera mínima entre duas consultas: a SEFAZ pede 1 h depois de "nenhum documento" (cStat 137)
* e bloqueia o CNPJ (656) se consultar antes. Os 5 min a mais cobrem a diferença de relógio.
*/
const INTERVALO_MINIMO_CAPTURA_MINUTOS = 65;
const MAXIMO_HORARIOS_CAPTURA = 12;
const MINUTOS_NO_DIA = 1440;
const horaMinutoSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:MM, de 00:00 a 23:59");
const DIAS_SEMANA = [
	"DOM",
	"SEG",
	"TER",
	"QUA",
	"QUI",
	"SEX",
	"SAB"
];
const diaSemanaSchema = z.enum(DIAS_SEMANA);
const diasSchema = z.array(diaSemanaSchema).min(1, "Escolha pelo menos um dia").refine((d) => new Set(d).size === d.length, "Dia repetido");
const intervaloMinutosSchema = z.number().int().min(65, `O intervalo mínimo é de ${String(65)} min`).max(MINUTOS_NO_DIA);
/** "HH:MM" → minutos desde 00:00. */
function minutosDoHorario(horario) {
	const [h = "0", m = "0"] = horario.split(":");
	return Number(h) * 60 + Number(m);
}
/** Minutos desde 00:00 → "HH:MM". */
function horarioDosMinutos(minutos) {
	const h = Math.floor(minutos / 60);
	return `${String(h).padStart(2, "0")}:${String(minutos % 60).padStart(2, "0")}`;
}
const MENSAGEM_ESPACO = `Deixe pelo menos ${String(65)} min entre as consultas: a SEFAZ bloqueia o CNPJ por 1 h se consultar antes.`;
const horariosSchema = z.array(horaMinutoSchema).min(1, "Informe pelo menos um horário").max(12, `No máximo ${String(12)} horários`).superRefine((horarios, ctx) => {
	const minutos = horarios.map(minutosDoHorario).sort((a, b) => a - b);
	if (new Set(minutos).size !== minutos.length) {
		ctx.addIssue({
			code: "custom",
			message: "Horário repetido"
		});
		return;
	}
	const primeiro = minutos[0] ?? 0;
	const ultimo = minutos.at(-1) ?? 0;
	if (minutos.some((m, i) => i > 0 && m - (minutos[i - 1] ?? 0) < 65) || minutos.length > 1 && primeiro + 1440 - ultimo < 65) ctx.addIssue({
		code: "custom",
		message: MENSAGEM_ESPACO
	});
});
const agendaHorariosSchema = z.object({
	tipo: z.literal("HORARIOS"),
	horarios: horariosSchema,
	dias: diasSchema
});
const agendaJanelaSchema = z.object({
	tipo: z.literal("JANELA"),
	inicio: horaMinutoSchema,
	fim: horaMinutoSchema,
	intervaloMinutos: intervaloMinutosSchema,
	dias: diasSchema
}).refine((j) => minutosDoHorario(j.inicio) < minutosDoHorario(j.fim), {
	message: "O início do expediente precisa ser antes do fim",
	path: ["fim"]
});
const agendaContinuaSchema = z.object({
	tipo: z.literal("CONTINUA"),
	intervaloMinutos: intervaloMinutosSchema
});
/**
* Quando buscar: horários fixos (08:00, 12:00, 16:45), de X em X minutos dentro de uma janela e
* dias da semana, ou o dia todo. Nenhum modo permite consultas a menos de 65 min uma da outra.
*/
const agendaCapturaSchema = z.discriminatedUnion("tipo", [
	agendaHorariosSchema,
	agendaJanelaSchema,
	agendaContinuaSchema
]);
/**
* Minutos do dia em que a agenda consulta (ordenados). A grade do "dia todo" parte de 00:00; o
* último horário que ficaria a menos de 65 min do primeiro do dia seguinte sai da grade, para
* nenhum modo consultar mais perto que isso (nem na virada do dia).
*/
function horariosDoDia(agenda) {
	if (agenda.tipo === "HORARIOS") return agenda.horarios.map(minutosDoHorario).sort((a, b) => a - b);
	const inicio = agenda.tipo === "JANELA" ? minutosDoHorario(agenda.inicio) : 0;
	const fim = agenda.tipo === "JANELA" ? minutosDoHorario(agenda.fim) : 1439;
	const minutos = [];
	for (let m = inicio; m <= fim; m += agenda.intervaloMinutos) minutos.push(m);
	while (minutos.length > 1 && inicio + 1440 - (minutos.at(-1) ?? 0) < 65) minutos.pop();
	return minutos;
}
/** Dias da semana em que a agenda consulta (o "dia todo" vale para todos). */
function diasDaAgenda(agenda) {
	return agenda.tipo === "CONTINUA" ? DIAS_SEMANA : agenda.dias;
}
const configuracaoCapturaSchema = z.object({
	ativa: z.boolean(),
	agenda: agendaCapturaSchema,
	/** Ciência da operação (210210) automática: é ela que libera o XML completo da nota. */
	cienciaAutomatica: z.boolean()
});
/** Padrão de quem nunca configurou: desligada, no expediente de dias úteis. */
const CONFIGURACAO_CAPTURA_PADRAO = {
	ativa: false,
	agenda: {
		tipo: "JANELA",
		inicio: "06:00",
		fim: "18:00",
		intervaloMinutos: 65,
		dias: [
			"SEG",
			"TER",
			"QUA",
			"QUI",
			"SEX"
		]
	},
	cienciaAutomatica: true
};
/** `GET/PUT …/captura/configuracao`: a configuração e a prévia que o agendador vai seguir. */
const configuracaoCapturaRespostaSchema = configuracaoCapturaSchema.extend({
	/** Próximas consultas (até 5), calculadas no servidor com o fuso da instalação. */
	proximasExecucoes: z.array(z.iso.datetime()).max(5),
	atualizadaEm: z.iso.datetime().nullable(),
	atualizadaPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	/** Fuso da instalação (IANA): os horários da agenda e da prévia são hora local dele. */
	fuso: z.string()
});
const situacaoCapturaSchema = z.enum([
	"DESLIGADA",
	"AGUARDANDO",
	"EXECUTANDO",
	"ESPERA_SEFAZ",
	"BLOQUEADA",
	"SEM_CERTIFICADO",
	"CERTIFICADO_VENCIDO",
	"ERRO"
]);
/**
* Estado da captura de uma empresa (`GET …/captura` e o painel `GET /captura`): a situação é
* derivada no servidor, com o motivo em texto para a tela.
*/
const estadoCapturaSchema = z.object({
	empresa: z.object({
		id: z.uuid(),
		nome: z.string(),
		cnpj: z.string()
	}),
	situacao: situacaoCapturaSchema,
	/** Frase curta da situação ("Bloqueada pela SEFAZ até 13:05"). */
	descricao: z.string(),
	ativa: z.boolean(),
	ultNsu: z.string(),
	maxNsu: z.string().nullable(),
	ultimaExecucaoEm: z.iso.datetime().nullable(),
	proximaExecucaoEm: z.iso.datetime().nullable(),
	/** Antes disto a distribuição não consulta (espera do 137 ou bloqueio). */
	liberadaEm: z.iso.datetime().nullable(),
	/** Bloqueio de verdade (656): também barra a busca por chave. */
	bloqueadaAte: z.iso.datetime().nullable(),
	ultimoCstat: z.string().nullable(),
	ultimoMotivo: z.string().nullable(),
	suspeitaOutroConsumidor: z.boolean(),
	consultasChaveNaHora: z.number().int().min(0),
	limiteConsultasChave: z.number().int(),
	fuso: z.string()
});
const painelCapturaSchema = z.object({ empresas: z.array(estadoCapturaSchema) });
/** `GET /api/v1/captura?empresaIds=…`: sem filtro, todas as empresas em que a pessoa lê documentos. */
const painelCapturaQuerySchema = z.object({ empresaIds: empresaIdsQuerySchema });
//#endregion
//#region src/captura-execucao.ts
/** Execuções da captura na SEFAZ (spec 0006, fatia 2): tipo, resultado e o que cada uma trouxe. */
const tipoExecucaoCapturaSchema = z.enum([
	"AGENDADA",
	"MANUAL",
	"CONSULTA_CHAVE"
]);
const statusExecucaoCapturaSchema = z.enum([
	"EXECUTANDO",
	"CONCLUIDA",
	"SEM_NOVIDADE",
	"BLOQUEADA",
	"SEM_CERTIFICADO",
	"CERTIFICADO_VENCIDO",
	"ERRO"
]);
const contador = z.number().int().min(0).default(0);
/** O que a execução trouxe: notas completas, resumos (chave aguardando XML), eventos e falhas. */
const contadoresCapturaSchema = z.object({
	importadas: contador,
	resumos: contador,
	eventos: contador,
	duplicadas: contador,
	ciencias: contador,
	erros: contador
});
const CONTADORES_CAPTURA_ZERADOS = {
	importadas: 0,
	resumos: 0,
	eventos: 0,
	duplicadas: 0,
	ciencias: 0,
	erros: 0
};
/** `POST …/captura/buscar-agora` → 202. O 409 traz `liberadaEm` quando a SEFAZ ainda não liberou. */
const buscarAgoraRespostaSchema = z.object({ execucaoId: z.uuid() });
/** Limite local de consultas por chave: metade dos 20/h da SEFAZ (spec 0006). */
const LIMITE_CONSULTAS_CHAVE_HORA = 10;
/** `POST …/captura/consultas-chave`: busca uma NF-e pela chave (consChNFe, até 90 dias). */
const consultaChaveRequestSchema = z.object({ chave: z.string().transform((v) => v.replace(/\s/g, "").toUpperCase()).pipe(z.string().regex(/^\d{6}[0-9A-Z]{12}\d{26}$/, "Chave de acesso inválida (44 posições)").refine(chaveValida, "Chave de acesso com dígito verificador inválido")) });
const resultadoConsultaChaveSchema = z.enum([
	"JA_EXISTE",
	"XML_COMPLETO",
	"RESUMO",
	"NAO_ENCONTRADA",
	"FORA_DO_PRAZO",
	"SEM_PERMISSAO",
	"ERRO"
]);
const consultaChaveRespostaSchema = z.object({
	resultado: resultadoConsultaChaveSchema,
	mensagem: z.string(),
	documentoId: z.uuid().nullable(),
	/** Ciência enviada agora para liberar o XML completo. */
	cienciaEnviada: z.boolean(),
	consultasNaHora: z.number().int().min(0),
	limite: z.literal(10)
});
/** Uma execução da captura (histórico): o que foi, quando, e o que trouxe. */
const execucaoCapturaSchema = z.object({
	id: z.uuid(),
	tipo: tipoExecucaoCapturaSchema,
	status: statusExecucaoCapturaSchema,
	iniciadaEm: z.iso.datetime(),
	concluidaEm: z.iso.datetime().nullable(),
	/** NSU como texto (até 15 dígitos; bigint não cabe em JSON). */
	nsuInicial: z.string(),
	nsuFinal: z.string().nullable(),
	paginas: z.number().int().min(0),
	cstat: z.string().nullable(),
	motivo: z.string().nullable(),
	contadores: contadoresCapturaSchema,
	disparadaPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable()
});
const listarExecucoesCapturaQuerySchema = paginacaoQuerySchema.extend({
	status: statusExecucaoCapturaSchema.optional(),
	tipo: tipoExecucaoCapturaSchema.optional()
});
const paginaExecucoesCapturaSchema = paginaNumeradaSchema(execucaoCapturaSchema);
//#endregion
//#region src/empresas.ts
/**
* Cadastro fiscal da empresa (docs/arquitetura/dados.md). Tudo que muda no tempo tem vigência:
* um documento é avaliado pelo regime/parâmetro vigente na data dele, não pelo atual.
*/
const regimeTributarioSchema = z.enum([
	"SIMPLES_NACIONAL",
	"MEI",
	"LUCRO_PRESUMIDO",
	"LUCRO_REAL"
]);
/** CRT da NF-e: 1 Simples, 2 Simples com excesso de sublimite, 3 Regime normal, 4 MEI. */
const crtSchema = z.enum([
	"1",
	"2",
	"3",
	"4"
]);
/**
* CRT deduzido do regime vigente (campo `CRT` do emitente, MOC NF-e). O 2 — Simples com excesso
* de sublimite da receita bruta — não se deduz do regime: fica para o fiscal informar.
*/
function crtDoRegime(regime) {
	switch (regime) {
		case "SIMPLES_NACIONAL": return "1";
		case "MEI": return "4";
		case "LUCRO_PRESUMIDO":
		case "LUCRO_REAL": return "3";
		case null: return null;
	}
}
const textoOpcional = (max) => z.string().trim().max(max).nullable().default(null);
const enderecoSchema = z.object({
	cep: z.string().regex(/^\d{8}$/, "CEP com 8 dígitos"),
	logradouro: z.string().trim().min(2).max(120),
	numero: z.string().trim().min(1).max(20),
	complemento: textoOpcional(60),
	bairro: z.string().trim().min(2).max(60),
	/** Código IBGE do município (7 dígitos) — é o que a NF-e usa (`cMun`). */
	codigoMunicipio: z.string().regex(/^\d{7}$/, "Código IBGE com 7 dígitos"),
	municipio: z.string().trim().min(2).max(60),
	uf: ufSchema
});
const regimeVigenciaSchema = z.object({
	regime: regimeTributarioSchema,
	inicio: dataSchema,
	fim: dataSchema.nullable().default(null)
});
const inscricaoEstadualSchema = z.object({
	uf: ufSchema,
	/** Só dígitos; "ISENTO" quando a empresa é isenta na UF. */
	inscricao: z.string().trim().regex(/^(\d{2,14}|ISENTO)$/, "IE com 2 a 14 dígitos ou ISENTO"),
	tipo: z.enum(["NORMAL", "SUBSTITUTO_TRIBUTARIO"])
});
const cnaeSchema = z.object({
	codigo: z.string().regex(/^\d{7}$/, "CNAE com 7 dígitos"),
	descricao: textoOpcional(200),
	principal: z.boolean()
});
const parametrosFiscaisSchema = z.object({
	contribuinteIcms: z.boolean(),
	contribuinteIpi: z.boolean(),
	regimePisCofins: z.enum(["CUMULATIVO", "NAO_CUMULATIVO"]).nullable(),
	obrigadaEfdIcmsIpi: z.boolean(),
	obrigadaEfdContribuicoes: z.boolean(),
	/** A partir de quando o FiscoAI é a fonte dos documentos desta empresa. */
	inicioNoFiscoai: dataSchema.nullable()
});
const contadorSchema = z.object({
	nome: z.string().trim().min(2).max(120),
	crc: textoOpcional(30),
	documento: textoOpcional(14),
	escritorio: textoOpcional(120),
	email: z.email().nullable().default(null),
	telefone: textoOpcional(20)
});
/** Id da empresa em cada sistema conectado — nunca uma coluna por sistema (ADR 0021). */
const codigoExternoSchema = z.object({
	conectorId: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
	codigo: z.string().trim().min(1).max(60)
});
/**
* Certificado A1 da empresa — SÓ os dados públicos (ADR 0018, spec 0005). O arquivo e a senha
* ficam cifrados no servidor e nenhuma rota os devolve.
*/
const certificadoResumoSchema = z.object({
	titular: z.string(),
	/** CNPJ do e-CNPJ (OID 2.16.76.1.3.3, DOC-ICP-04). */
	documento: z.string(),
	emissor: z.string().nullable(),
	numeroSerie: z.string().nullable(),
	validoDe: z.iso.datetime().nullable(),
	validoAte: z.iso.datetime(),
	/** SHA-256 do certificado, em hexadecimal maiúsculo. */
	thumbprint: z.string(),
	enviadoEm: z.iso.datetime().nullable(),
	enviadoPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	/** Avisos registrados no envio (outro estabelecimento da mesma raiz, cadeia não conferida). */
	avisos: z.array(z.string()),
	/** Primeira vez que a SEFAZ aceitou o A1 numa consulta (a cadeia ICP-Brasil foi conferida lá). */
	aceitoSefazEm: z.iso.datetime().nullable()
});
const PENDENCIAS_CADASTRO = [
	"SEM_REGIME_VIGENTE",
	"SEM_INSCRICAO_ESTADUAL",
	"SEM_CNAE_PRINCIPAL",
	"SEM_CONTADOR",
	"SEM_ENDERECO",
	"CERTIFICADO_VENCENDO",
	"CERTIFICADO_VENCIDO"
];
const pendenciaCadastroSchema = z.enum(PENDENCIAS_CADASTRO);
const empresaResumoSchema = z.object({
	id: z.uuid(),
	cnpj: z.string(),
	razaoSocial: z.string(),
	nomeFantasia: z.string().nullable(),
	uf: ufSchema.nullable(),
	municipio: z.string().nullable(),
	regimeVigente: regimeTributarioSchema.nullable(),
	ativa: z.boolean(),
	/** Matriz e filiais compartilham a raiz (8 primeiras posições do CNPJ). */
	matriz: z.boolean(),
	pendencias: z.array(pendenciaCadastroSchema),
	quantidadeDocumentos: z.number().int().nonnegative(),
	/** Vencimento do certificado A1 (nulo = sem certificado) — saúde dos certificados no Início. */
	certificadoValidoAte: z.iso.datetime().nullable()
});
const empresaDetalheSchema = empresaResumoSchema.extend({
	inscricaoMunicipal: z.string().nullable(),
	crt: crtSchema.nullable(),
	email: z.string().nullable(),
	telefone: z.string().nullable(),
	inicioAtividades: dataSchema.nullable(),
	endereco: enderecoSchema.nullable(),
	regimes: z.array(regimeVigenciaSchema),
	inscricoesEstaduais: z.array(inscricaoEstadualSchema),
	cnaes: z.array(cnaeSchema),
	parametrosFiscais: parametrosFiscaisSchema.nullable(),
	contador: contadorSchema.nullable(),
	certificado: certificadoResumoSchema.nullable(),
	codigosExternos: z.array(codigoExternoSchema),
	criadaEm: z.iso.datetime(),
	atualizadaEm: z.iso.datetime()
});
/** Campos gerais sem valor padrão: num PATCH, ausente = "não mexer", nunca "apagar". */
const camposGeraisEmpresaSchema = z.object({
	razaoSocial: z.string().trim().min(2).max(150),
	nomeFantasia: z.string().trim().max(150).nullable(),
	inscricaoMunicipal: z.string().trim().max(20).nullable(),
	crt: crtSchema.nullable(),
	email: z.email().nullable(),
	telefone: z.string().trim().max(20).nullable(),
	inicioAtividades: dataSchema.nullable(),
	endereco: enderecoSchema.nullable()
});
/** `POST /api/v1/empresas` (plataforma.empresas.gerenciar). Opcional ausente = nulo. */
const criarEmpresaRequestSchema = camposGeraisEmpresaSchema.extend({
	cnpj: cnpjSchema,
	regime: regimeVigenciaSchema.nullable()
}).partial({
	nomeFantasia: true,
	inscricaoMunicipal: true,
	crt: true,
	email: true,
	telefone: true,
	inicioAtividades: true,
	endereco: true,
	regime: true
});
/** `PATCH /api/v1/empresas/:id` (empresa.cadastro.editar). CNPJ não muda. */
const atualizarEmpresaRequestSchema = camposGeraisEmpresaSchema.extend({ ativa: z.boolean() }).partial().refine((corpo) => Object.keys(corpo).length > 0, "Nada para alterar");
/** Sub-recursos substituídos por inteiro: `PUT /api/v1/empresas/:id/<recurso>`. */
const substituirRegimesRequestSchema = z.object({ itens: z.array(regimeVigenciaSchema).max(50) });
const substituirInscricoesRequestSchema = z.object({ itens: z.array(inscricaoEstadualSchema).max(27) });
const substituirCnaesRequestSchema = z.object({ itens: z.array(cnaeSchema).max(100) });
const substituirCodigosExternosRequestSchema = z.object({ itens: z.array(codigoExternoSchema).max(20) });
const definirParametrosFiscaisRequestSchema = parametrosFiscaisSchema;
const definirContadorRequestSchema = contadorSchema.nullable();
const listarEmpresasQuerySchema = z.object({
	q: z.string().trim().max(120).optional(),
	ativa: z.enum(["true", "false"]).transform((valor) => valor === "true").optional(),
	comPendencia: z.enum(["true", "false"]).transform((valor) => valor === "true").optional()
});
//#endregion
//#region src/cartao-cnpj.ts
/**
* "Cartão CNPJ": dados públicos da Receita Federal para preencher e atualizar o cadastro
* (docs/especificacoes/0002-cartao-cnpj.md). Vem de um provedor externo atrás de uma porta —
* pode ser desligado na instalação. Sócios (QSA) NÃO entram: dado pessoal sem uso fiscal (LGPD).
*/
const enderecoCartaoSchema = z.object({
	cep: z.string(),
	/** Já com o tipo ("AVENIDA PRAIA DE ITAPOAN"). */
	logradouro: z.string(),
	numero: z.string(),
	complemento: z.string().nullable(),
	bairro: z.string(),
	codigoMunicipio: z.string(),
	municipio: z.string(),
	uf: ufSchema
});
const cartaoCnpjSchema = z.object({
	cnpj: z.string(),
	razaoSocial: z.string(),
	nomeFantasia: z.string().nullable(),
	matriz: z.boolean(),
	/** Como a Receita informa: ATIVA, BAIXADA, INAPTA, SUSPENSA, NULA. */
	situacaoCadastral: z.string(),
	situacaoDesde: dataSchema.nullable(),
	ativa: z.boolean(),
	naturezaJuridica: z.string().nullable(),
	porte: z.string().nullable(),
	inicioAtividades: dataSchema.nullable(),
	endereco: enderecoCartaoSchema.nullable(),
	telefone: z.string().nullable(),
	email: z.string().nullable(),
	/** Principal primeiro. */
	cnaes: z.array(cnaeSchema),
	/**
	* SUGESTÃO de vigências de regime: períodos do Simples/MEI (datas de opção e exclusão) e a forma
	* de tributação declarada na ECF ano a ano. A última vigência fica aberta (fim nulo) — a ECF é de
	* anos passados; o fiscal confirma antes de gravar.
	*/
	regimesSugeridos: z.array(regimeVigenciaSchema),
	/** CRT do regime sugerido vigente (`crtDoRegime`); o 2 nunca é sugerido. */
	crtSugerido: crtSchema.nullable(),
	/** Último ano com forma de tributação declarada (ECF), para o fiscal saber o quão recente é. */
	ultimoAnoDeclarado: z.number().int().nullable(),
	/**
	* A fonte informa a forma de tributação declarada (ECF)? A reserva (CNPJ.ws) não: aí os regimes
	* sugeridos vêm só do Simples/MEI e não servem para substituir um regime já cadastrado.
	*/
	ecfInformada: z.boolean(),
	/** Inscrições estaduais ativas que a fonte informa (a BrasilAPI não informa: lista vazia). */
	inscricoesEstaduais: z.array(inscricaoEstadualSchema),
	fonte: z.string(),
	consultadoEm: z.iso.datetime()
});
//#endregion
//#region src/catalogo-permissoes.ts
/** Rótulos pt-BR do catálogo para as telas de perfis (docs/arquitetura/controle-de-acesso.md). */
const ROTULOS_PERMISSOES = {
	"plataforma.usuarios.gerenciar": {
		grupo: "Plataforma",
		rotulo: "Gerenciar usuários"
	},
	"plataforma.perfis.gerenciar": {
		grupo: "Plataforma",
		rotulo: "Gerenciar perfis"
	},
	"plataforma.empresas.gerenciar": {
		grupo: "Plataforma",
		rotulo: "Cadastrar e desativar empresas"
	},
	"plataforma.empresas.acessar_todas": {
		grupo: "Plataforma",
		rotulo: "Acessar todas as empresas"
	},
	"plataforma.auditoria.ler": {
		grupo: "Plataforma",
		rotulo: "Ler a auditoria"
	},
	"plataforma.usuarios.ler": {
		grupo: "Plataforma",
		rotulo: "Ver os usuários cadastrados"
	},
	"empresa.cadastro.ler": {
		grupo: "Empresa",
		rotulo: "Ver o cadastro fiscal"
	},
	"empresa.cadastro.editar": {
		grupo: "Empresa",
		rotulo: "Editar o cadastro fiscal"
	},
	"empresa.certificado.gerenciar": {
		grupo: "Empresa",
		rotulo: "Gerenciar o certificado digital"
	},
	"empresa.membros.gerenciar": {
		grupo: "Empresa",
		rotulo: "Gerenciar quem acessa a empresa"
	},
	"documentos.ler": {
		grupo: "Documentos",
		rotulo: "Ver documentos fiscais"
	},
	"documentos.importar": {
		grupo: "Documentos",
		rotulo: "Importar XML, ZIP e planilhas"
	},
	"documentos.baixar": {
		grupo: "Documentos",
		rotulo: "Baixar XML e DANFE"
	},
	"documentos.classificar": {
		grupo: "Documentos",
		rotulo: "Classificar documentos"
	},
	"documentos.remover": {
		grupo: "Documentos",
		rotulo: "Remover documentos (com motivo)"
	},
	"documentos.exportar": {
		grupo: "Documentos",
		rotulo: "Exportar listas e pacotes"
	},
	"chaves.gerenciar": {
		grupo: "Documentos",
		rotulo: "Gerenciar chaves pendentes"
	},
	"conciliacao.ler": {
		grupo: "Conciliação",
		rotulo: "Ver vínculos com pedidos e lançamentos"
	},
	"conciliacao.vincular": {
		grupo: "Conciliação",
		rotulo: "Vincular e desvincular"
	},
	"validacoes.solicitar": {
		grupo: "Validações",
		rotulo: "Solicitar validação a compradores"
	},
	"validacoes.responder": {
		grupo: "Validações",
		rotulo: "Responder validações atribuídas"
	},
	"relatorios.ler": {
		grupo: "Relatórios",
		rotulo: "Ver e exportar relatórios"
	},
	"integracoes.gerenciar": {
		grupo: "Integrações",
		rotulo: "Configurar conectores da empresa"
	},
	"ia.analisar": {
		grupo: "IA",
		rotulo: "Usar análises assistidas por IA"
	},
	"empresa.captura.gerenciar": {
		grupo: "Empresa",
		rotulo: "Configurar a captura automática na SEFAZ"
	},
	"documentos.captura.executar": {
		grupo: "Documentos",
		rotulo: "Buscar notas na SEFAZ agora ou pela chave"
	}
};
const LEITURA_EMPRESA = [
	"empresa.cadastro.ler",
	"documentos.ler",
	"documentos.baixar",
	"documentos.exportar",
	"conciliacao.ler",
	"relatorios.ler"
];
/** Responder validação é do comprador; quem cobra (fiscal) não responde a si mesmo. */
const FISCAL_LIDER = PERMISSOES_EMPRESA.filter((p) => p !== "validacoes.responder");
/**
* Fiscal líder menos o que é administrativo ou irreversível. O cadastro da empresa (parâmetros,
* regras de classificação, centros de custo) o fiscal só visualiza (Igor, 15/10/2026).
*/
const FISCAL = FISCAL_LIDER.filter((p) => p !== "empresa.cadastro.editar" && p !== "empresa.membros.gerenciar" && p !== "empresa.certificado.gerenciar" && p !== "empresa.captura.gerenciar" && p !== "documentos.remover" && p !== "integracoes.gerenciar");
/** Administrador sem gerenciar usuários e perfis: esses ficam com o admin (só os vê). */
const GESTAO_FISCAL = [...PERMISSOES_PLATAFORMA.filter((p) => p !== "plataforma.usuarios.gerenciar" && p !== "plataforma.perfis.gerenciar")];
/** Definição dos perfis de sistema — o seed grava exatamente isto. */
const PERFIS_SISTEMA = {
	admin: {
		nome: "Administrador",
		escopo: "PLATAFORMA",
		permissoes: [...PERMISSOES_PLATAFORMA],
		descricao: "Administra a instalação: usuários, perfis, empresas e auditoria."
	},
	fiscal_lider: {
		nome: "Fiscal líder",
		escopo: "EMPRESA",
		permissoes: FISCAL_LIDER,
		descricao: "Tudo do fiscal, mais o cadastro da empresa, membros, certificado, captura na SEFAZ, integrações e remoção de documentos."
	},
	fiscal: {
		nome: "Fiscal",
		escopo: "EMPRESA",
		permissoes: FISCAL,
		descricao: "Importa, confere, classifica e concilia os documentos da empresa; o cadastro da empresa só visualiza."
	},
	contabil: {
		nome: "Contábil",
		escopo: "EMPRESA",
		permissoes: LEITURA_EMPRESA,
		descricao: "Contador: lê e exporta, sem alterar."
	},
	comprador_validador: {
		nome: "Comprador validador",
		escopo: "EMPRESA",
		permissoes: ["documentos.ler", "validacoes.responder"],
		descricao: "Responde às validações de compra atribuídas a ele."
	},
	auditor: {
		nome: "Auditor",
		escopo: "EMPRESA",
		permissoes: LEITURA_EMPRESA,
		descricao: "Lê e exporta tudo na empresa, para conferência e auditoria."
	},
	gestao_fiscal: {
		nome: "Gestão fiscal",
		escopo: "PLATAFORMA",
		permissoes: GESTAO_FISCAL,
		descricao: "Responsável pelo fiscal: cadastra as empresas e age em todas, inclusive certificados. Vê os usuários, mas usuários e perfis ficam com o administrador."
	}
};
//#endregion
//#region src/centros-custo.ts
/**
* Centro de custo informado no FiscoAI (spec 0004, especialista contábil 02/10/2026). Cadastro por
* empresa; a natureza decide o grupo contábil de uso e consumo e serviço (Lei 6.404/76, art. 187,
* II e III; CPC 26, itens 99–103). Nenhuma integração: um conector poderá preencher no futuro.
*/
const naturezaCentroCustoSchema = z.enum(["CUSTO", "DESPESA"]);
/**
* Como o dado entrou: digitado, planilha importada, (futuro) conector ou, no item, uma regra de
* classificação da empresa (spec 0007).
*/
const origemCadastroSchema = z.enum([
	"MANUAL",
	"IMPORTACAO",
	"CONECTOR",
	"REGRA"
]);
const camposCentroCusto = {
	codigo: z.string().trim().min(1, "Informe o código").max(40, "Até 40 caracteres").regex(/^[\w.\-/]+$/, "Use letras, números, ponto, hífen, barra ou sublinhado"),
	descricao: z.string().trim().min(1, "Informe a descrição").max(120, "Até 120 caracteres"),
	natureza: naturezaCentroCustoSchema
};
const centroCustoSchema = z.object({
	id: z.uuid(),
	empresaId: z.uuid(),
	...camposCentroCusto,
	ativo: z.boolean(),
	origem: origemCadastroSchema,
	/** Itens de notas que usam este centro de custo. */
	usos: z.number().int().nonnegative(),
	criadoEm: z.iso.datetime(),
	atualizadoEm: z.iso.datetime()
});
/** Forma curta, embutida na classificação do item. */
const centroCustoResumoSchema = centroCustoSchema.pick({
	id: true,
	codigo: true,
	descricao: true,
	natureza: true,
	ativo: true
});
/** `POST /empresas/:id/centros-custo` (empresa.cadastro.editar). */
const criarCentroCustoRequestSchema = z.object(camposCentroCusto);
/** `PATCH /empresas/:id/centros-custo/:ccId` — o código não muda (é a identidade na planilha). */
const atualizarCentroCustoRequestSchema = z.object({
	descricao: camposCentroCusto.descricao,
	natureza: naturezaCentroCustoSchema,
	ativo: z.boolean()
}).partial().refine((c) => Object.keys(c).length > 0, "Nada para alterar");
/** `GET /empresas/:id/centros-custo` (empresa.cadastro.ler) — busca no servidor (select remoto). */
const listarCentrosCustoQuerySchema = z.object({
	q: z.string().trim().max(60).optional(),
	/** `true` só ativos (escolha na nota); ausente traz todos (cadastro). */
	ativos: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
});
const paginaCentrosCustoSchema = cursorPageSchema(centroCustoSchema);
const CABECALHO_CSV_CENTROS_CUSTO = [
	"codigo",
	"descricao",
	"natureza",
	"ativo"
];
/**
* `POST /empresas/:id/centros-custo/importacao?confirmar=true|false` (multipart, campo `arquivo`).
* Sem `confirmar` só pré-visualiza; com erro em alguma linha nada é gravado. Upsert por código,
* nunca exclui (o que não está no arquivo fica como está).
*/
const importarCentrosCustoQuerySchema = z.object({ confirmar: z.enum(["true", "false"]).default("false").transform((v) => v === "true") });
const linhaImportacaoCentroCustoSchema = z.object({
	linha: z.number().int().positive(),
	codigo: z.string(),
	acao: z.enum([
		"CRIAR",
		"ATUALIZAR",
		"SEM_MUDANCA",
		"ERRO"
	]),
	erros: z.array(z.string())
});
const resultadoImportacaoCentrosCustoSchema = z.object({
	confirmado: z.boolean(),
	linhas: z.array(linhaImportacaoCentroCustoSchema),
	totais: z.object({
		criar: z.number().int().nonnegative(),
		atualizar: z.number().int().nonnegative(),
		semMudanca: z.number().int().nonnegative(),
		erros: z.number().int().nonnegative()
	})
});
/**
* `PUT /api/v1/documentos/:id/centro-custo` (documentos.classificar): CC padrão da nota (`todos`)
* e/ou exceção por item (`itens`, que prevalecem). Nulo limpa. Só centro de custo ATIVO da
* empresa do vínculo (422 caso contrário). Um CC por item — sem rateio (spec 0004).
*/
const definirCentroCustoRequestSchema = z.object({
	todos: z.uuid().nullable().optional(),
	itens: z.array(z.object({
		nItem: z.number().int().positive(),
		centroCustoId: z.uuid().nullable()
	})).max(990).optional()
}).refine((c) => c.todos !== void 0 || (c.itens?.length ?? 0) > 0, "Nada para definir");
//#endregion
//#region src/certificados.ts
/**
* Envio do certificado A1 (spec 0005, ADR 0018): `PUT /api/v1/empresas/:id/certificado`
* (empresa.certificado.gerenciar), multipart com o campo `arquivo` (PKCS#12, até 64 KB) e o campo
* `senha`. Substitui o anterior. `DELETE` remove. Nenhuma rota devolve o arquivo nem a senha.
*/
const LIMITE_ARQUIVO_CERTIFICADO = 65536;
const EXTENSOES_CERTIFICADO = [".pfx", ".p12"];
/** Motivos de recusa no envio — mensagens fixas, nunca o erro cru da biblioteca. */
const recusaCertificadoSchema = z.enum([
	"ARQUIVO_INVALIDO",
	"SENHA_INVALIDA",
	"SEM_CHAVE_PRIVADA",
	"CHAVE_NAO_CONFERE",
	"NAO_E_ECNPJ",
	"CNPJ_DE_OUTRA_EMPRESA",
	"VENCIDO",
	"AINDA_NAO_VALIDO",
	"CHAVE_FRACA",
	"ASSINATURA_FRACA",
	"SEM_USO_ASSINATURA",
	"CERTIFICADO_DE_AC",
	"FORA_DA_ICP_BRASIL"
]);
const MENSAGEM_RECUSA = {
	ARQUIVO_INVALIDO: "O arquivo não é um certificado A1 (PKCS#12).",
	SENHA_INVALIDA: "A senha não abre este certificado.",
	SEM_CHAVE_PRIVADA: "O arquivo não traz a chave privada — exporte o A1 com a chave.",
	CHAVE_NAO_CONFERE: "A chave privada não corresponde ao certificado.",
	NAO_E_ECNPJ: "Não é um e-CNPJ: o certificado não traz o CNPJ de uma empresa.",
	CNPJ_DE_OUTRA_EMPRESA: "O certificado é de outra empresa (raiz do CNPJ diferente).",
	VENCIDO: "O certificado está vencido.",
	AINDA_NAO_VALIDO: "O certificado ainda não está no período de validade.",
	CHAVE_FRACA: "Chave fraca: a SEFAZ exige RSA de 2048 bits ou mais.",
	ASSINATURA_FRACA: "O certificado foi assinado com algoritmo fraco (MD5 ou SHA-1).",
	SEM_USO_ASSINATURA: "O certificado não serve para assinatura e autenticação de cliente.",
	CERTIFICADO_DE_AC: "É o certificado de uma autoridade certificadora, não o da empresa.",
	FORA_DA_ICP_BRASIL: "O certificado não foi emitido pela ICP-Brasil — a SEFAZ não o aceita."
};
/** Dias antes do vencimento em que o aviso aparece (60, 30, 15 e 7). */
const AVISOS_VENCIMENTO_DIAS = [
	60,
	30,
	15,
	7
];
//#endregion
//#region src/classificacao.ts
/**
* Etapa 2 (docs/especificacoes/0003-etapa-2-leitura-fiscal.md): classificação por ITEM, CFOP de
* entrada derivado, conferências fixas e pendências. Regras validadas pelos especialistas
* (docs/dominio-fiscal/cfop-de-entrada.md e conferencias.md).
*/
/** Destino do item na empresa — decide o CFOP de entrada, o grupo contábil e o patrimônio. */
const classificacaoItemSchema = z.enum([
	"REVENDA",
	"INSUMO_INDUSTRIALIZACAO",
	"USO_CONSUMO",
	"ATIVO",
	"INSUMO_SERVICO_COMUNICACAO",
	"SERVICO",
	"COMBUSTIVEL",
	"PERSONALIZADA"
]);
/**
* Spec 0011: a classificação da NOTA, resumo dos itens quando todos estão classificados. Itens com
* classificações diferentes (ou personalizadas diferentes) dão `MISTA`.
*/
const classificacaoNotaSchema = z.enum([...classificacaoItemSchema.options, "MISTA"]);
/** A classificação da nota na lista; `itens` (as classificações distintas) serve à dica da MISTA. */
const classificacaoDaNotaSchema = z.object({
	tipo: classificacaoNotaSchema,
	/** A classificação da empresa, quando todos os itens têm a mesma personalizada. */
	personalizada: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	itens: z.array(z.object({
		classificacao: classificacaoItemSchema,
		personalizada: z.string().nullable()
	}))
});
const grupoContabilSchema = z.enum([
	"ATIVO",
	"CUSTO",
	"DESPESA"
]);
/**
* De onde veio o grupo contábil. Precedência: REGRA (ativo, revenda, insumos) > CENTRO_CUSTO
* (natureza do CC, para uso e consumo e serviço) > PADRAO_SEM_CC (parâmetro da empresa).
*/
const origemGrupoSchema = z.enum([
	"REGRA",
	"CENTRO_CUSTO",
	"PADRAO_SEM_CC",
	"MANUAL"
]);
/** CFOP de entrada derivado — ou o motivo de não haver regra segura (nunca um chute). */
const cfopEntradaSchema = z.discriminatedUnion("tipo", [
	z.object({
		tipo: z.literal("DERIVADO"),
		cfop: z.string().regex(/^[123]\d{3}$/)
	}),
	z.object({
		tipo: z.literal("COPIADO"),
		cfop: z.string().regex(/^[123]\d{3}$/)
	}),
	z.object({
		tipo: z.literal("SEM_REGRA"),
		motivo: z.string()
	})
]);
const classificacaoDoItemSchema = z.object({
	nItem: z.number().int().positive(),
	classificacao: classificacaoItemSchema.nullable(),
	cfopEntrada: cfopEntradaSchema.nullable(),
	grupoContabil: grupoContabilSchema.nullable(),
	origemGrupo: origemGrupoSchema.nullable(),
	patrimonio: z.boolean().nullable(),
	/** Centro de custo informado pelo fiscal (padrão da nota ou exceção do item). */
	centroCusto: centroCustoResumoSchema.nullable(),
	/** Valor contábil do item: vProd − vDesc + vFrete + vSeg + vOutro + vIPI + vST + vFCPST. */
	valorContabil: decimalSchema,
	classificadoPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	classificadoEm: z.iso.datetime().nullable(),
	/** Spec 0007: quem decidiu a classificação (nulo enquanto o item está a classificar). */
	origemClassificacao: z.enum(["MANUAL", "REGRA"]).nullable(),
	/** A regra da empresa que classificou o item (só com origem REGRA). */
	regra: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	/** A classificação personalizada da empresa (só quando `classificacao` é PERSONALIZADA). */
	personalizada: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	alertas: z.array(z.string())
});
/** Resumo da classificação da nota (derivado dos itens). */
const statusClassificacaoSchema = z.enum([
	"NAO_SE_APLICA",
	"PENDENTE",
	"PARCIAL",
	"COMPLETA"
]);
/**
* `PUT /api/v1/documentos/:id/classificacao` (documentos.classificar): classifica a nota inteira
* (`todos`) e/ou itens específicos (`itens`, que prevalecem). Responde o detalhe atualizado.
*/
const classificarDocumentoRequestSchema = z.object({
	todos: classificacaoItemSchema.optional(),
	/** Com `todos: 'PERSONALIZADA'`, a classificação da empresa para a nota inteira. */
	todosPersonalizadaId: z.uuid().optional(),
	itens: z.array(z.object({
		nItem: z.number().int().positive(),
		classificacao: classificacaoItemSchema.nullable(),
		/** Obrigatório quando a classificação é PERSONALIZADA (spec 0009). */
		classificacaoPersonalizadaId: z.uuid().nullable().default(null)
	}).refine((i) => i.classificacao === "PERSONALIZADA" === (i.classificacaoPersonalizadaId !== null), "Classificação personalizada exige a classificação da empresa")).max(990).optional()
}).refine((c) => c.todos !== void 0 || (c.itens?.length ?? 0) > 0, "Nada para classificar").refine((c) => c.todos === "PERSONALIZADA" === (c.todosPersonalizadaId !== void 0), "Classificação personalizada exige a classificação da empresa");
const codigoConferenciaSchema = z.enum([
	"C01_ASSINATURA",
	"C02_PROTOCOLO",
	"C03_AMBIENTE",
	"C04_CHAVE",
	"C05_SITUACAO",
	"C06_DESTINATARIO_DO_GRUPO",
	"C07_DESTINATARIO_CADASTRO",
	"C08_MESMA_CHAVE",
	"C09_DUPLICIDADE_NUMERO",
	"C10_TOTAIS",
	"C11_DATA_EMISSAO",
	"C12_CFOP_OPERACAO",
	"C13_NCM",
	"C14_ST",
	"C15_SIMPLES",
	"C16_DIFAL"
]);
const severidadeSchema = z.enum([
	"ERRO",
	"ALERTA",
	"INFORMACAO"
]);
/** Só as conferências que acusaram algo (as que passam não são listadas). */
const resultadoConferenciaSchema = z.object({
	codigo: codigoConferenciaSchema,
	severidade: severidadeSchema,
	mensagem: z.string(),
	/** Item afetado, quando a conferência é por item. */
	nItem: z.number().int().positive().nullable()
});
const tipoPendenciaSchema = z.enum([
	"CHAVE_SEM_XML",
	"CANCELADA_JA_CLASSIFICADA",
	"ITEM_SEM_CLASSIFICACAO",
	"SEM_EMPRESA_DO_GRUPO",
	"SITUACAO_DESCONHECIDA",
	"EMPRESA_SEM_REGIME",
	"CONFERENCIA_ERRO",
	"CFOP_SEM_REGRA",
	"NOTA_PROPRIA_OU_ENTRADA_DO_EMITENTE",
	"ALERTA_CLASSIFICACAO",
	"CCE_RECEBIDA",
	"CENTRO_CUSTO_AUSENTE",
	"CENTRO_CUSTO_INATIVO",
	"ATIVO_SEM_CENTRO_CUSTO",
	"CERTIFICADO_VENCENDO",
	"CERTIFICADO_VENCIDO",
	"CIENCIA_SEM_CONCLUSAO",
	"XML_FORA_DO_PRAZO_CIENCIA"
]);
const prioridadeSchema = z.enum([
	"ALTA",
	"MEDIA",
	"BAIXA"
]);
const pendenciaSchema = z.object({
	tipo: tipoPendenciaSchema,
	prioridade: prioridadeSchema,
	empresaId: z.uuid().nullable(),
	/** `documento_empresa` quando há documento; nulo para chave sem XML. */
	documentoId: z.uuid().nullable(),
	chave: z.string().nullable(),
	emitente: z.string().nullable(),
	valor: decimalSchema.nullable(),
	emitidaEm: z.string().nullable(),
	idadeDias: z.number().int().nonnegative().nullable(),
	mensagem: z.string(),
	acaoSugerida: z.string()
});
/** `GET /api/v1/pendencias` — mesmo escopo de empresas; ordem: prioridade, depois idade. */
const listarPendenciasQuerySchema = z.object({
	empresaIds: empresaIdsQuerySchema,
	emitidaDe: z.iso.date().optional(),
	emitidaAte: z.iso.date().optional(),
	prioridade: prioridadeSchema.optional(),
	tipo: tipoPendenciaSchema.optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(100)
});
const resumoPendenciasSchema = z.object({
	total: z.number().int().nonnegative(),
	porPrioridade: z.record(prioridadeSchema, z.number().int().nonnegative()),
	porTipo: z.record(tipoPendenciaSchema, z.number().int().nonnegative())
});
/**
* `GET /api/v1/relatorios/planilha-fiscal.xlsx` (relatorios.ler) — abas Notas (16 colunas + apoio),
* Itens, Resumo, Pendências e Parâmetros. Período pela data de emissão (local do dhEmi).
*/
const planilhaFiscalQuerySchema = z.object({
	empresaIds: empresaIdsQuerySchema,
	emitidaDe: z.iso.date(),
	emitidaAte: z.iso.date(),
	direcao: z.enum(["ENTRADA", "SAIDA"]).optional()
});
const parametrosClassificacaoSchema = z.object({
	/** Grupo contábil de uso e consumo/serviço enquanto não há centro de custo (padrão DESPESA). */
	grupoPadraoSemCentroCusto: z.enum(["CUSTO", "DESPESA"]),
	/** Habilita a classe INSUMO_SERVICO_COMUNICACAO (x126) — depende da pergunta 13 ao fiscal. */
	usaInsumoServicoComunicacao: z.boolean(),
	/** C11: alerta para notas emitidas há mais de N dias quando importadas. */
	diasAlertaEmissaoAntiga: z.number().int().min(1).max(3650),
	/** Limite de valor unitário para o alerta de coerência Ativo × uso e consumo (RIR/2018, art. 313). */
	limiteAtivoValorUnitario: decimalSchema
});
const PARAMETROS_CLASSIFICACAO_PADRAO = {
	grupoPadraoSemCentroCusto: "DESPESA",
	usaInsumoServicoComunicacao: false,
	diasAlertaEmissaoAntiga: 30,
	limiteAtivoValorUnitario: "1200.00"
};
//#endregion
//#region src/classificacoes-personalizadas.ts
/**
* Classificações personalizadas por empresa (spec 0009): a fiscal cadastra a classificação com o
* CFOP de entrada, o grupo contábil e o patrimônio que ela pede — nada disso fica no código.
* Ex.: "Repasse recarga MVNO": entrada x949 para os CFOPs 5949/6949 do fornecedor, grupo Custo.
*/
/** CFOP de entrada: um final fixo (1º dígito pela UF) para os CFOPs do fornecedor da lista, ou o mesmo do fornecedor. */
const cfopEntradaPersonalizadaSchema = z.discriminatedUnion("tipo", [z.object({
	tipo: z.literal("FINAL"),
	final: z.string().regex(/^\d{3}$/, "Final do CFOP: 3 dígitos (ex.: 949)"),
	cfopsFornecedor: z.array(z.string().regex(/^[56]\d{3}$/, "CFOP de saída: 4 dígitos começando por 5 ou 6")).min(1, "Informe pelo menos um CFOP do fornecedor").max(20)
}), z.object({ tipo: z.literal("MESMO") })]);
/** Grupo contábil fixo, ou pela natureza do centro de custo (como uso e consumo). */
const grupoPersonalizadaSchema = z.enum([
	"ATIVO",
	"CUSTO",
	"DESPESA",
	"CENTRO_CUSTO"
]);
const classificacaoPersonalizadaEntradaSchema = z.object({
	nome: z.string().trim().min(1).max(60),
	ativa: z.boolean().default(true),
	cfopEntrada: cfopEntradaPersonalizadaSchema,
	grupo: grupoPersonalizadaSchema,
	patrimonio: z.boolean().default(false)
});
const classificacaoPersonalizadaSchema = classificacaoPersonalizadaEntradaSchema.extend({
	id: z.uuid(),
	/** Itens classificados com ela hoje. */
	itens: z.number().int().nonnegative(),
	/** Regras que apontam para ela. */
	regras: z.number().int().nonnegative(),
	atualizadaEm: z.iso.datetime()
});
const listaClassificacoesPersonalizadasSchema = z.object({ items: z.array(classificacaoPersonalizadaSchema) });
/** Referência curta (item classificado, regra). */
const personalizadaResumoSchema = z.object({
	id: z.uuid(),
	nome: z.string()
});
//#endregion
//#region src/diretorio.ts
/**
* Gestão de acesso (ADR 0007/0008): usuários e perfis globais, vínculos usuário↔empresa.
* Perfis são referenciados pela `chave` (estável), nunca pelo id interno.
*/
const nomeSchema = z.string().trim().min(2, "Informe o nome").max(120);
const perfilRefSchema = z.object({
	chave: z.string(),
	nome: z.string()
});
const usuarioResumoSchema = z.object({
	id: z.uuid(),
	nome: z.string(),
	email: z.email(),
	ativo: z.boolean(),
	perfilPlataforma: perfilRefSchema.nullable(),
	quantidadeEmpresas: z.number().int().nonnegative(),
	ultimoLoginEm: z.iso.datetime().nullable(),
	criadoEm: z.iso.datetime()
});
const listarUsuariosQuerySchema = z.object({
	q: z.string().trim().max(120).optional(),
	ativo: z.enum(["true", "false"]).transform((valor) => valor === "true").optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(100).default(30)
});
const criarUsuarioRequestSchema = z.object({
	nome: nomeSchema,
	email: emailSchema,
	chavePerfilPlataforma: chavePerfilSchema.nullable().default(null),
	senha: senhaSchema
});
const atualizarUsuarioRequestSchema = z.object({
	nome: nomeSchema,
	/** Desativar corta o acesso a todas as empresas e encerra as sessões. */
	ativo: z.boolean(),
	chavePerfilPlataforma: chavePerfilSchema.nullable()
}).partial().refine((corpo) => Object.keys(corpo).length > 0, "Nada para alterar");
const definirSenhaRequestSchema = z.object({ senha: senhaSchema });
const perfilSchema = z.object({
	id: z.uuid(),
	chave: z.string(),
	nome: z.string(),
	descricao: z.string().nullable(),
	escopo: escopoPerfilSchema,
	permissoes: z.array(permissaoSchema),
	sistema: z.boolean(),
	/** Quantos vínculos (empresa) ou usuários (plataforma) usam o perfil. */
	emUso: z.number().int().nonnegative()
});
const criarPerfilRequestSchema = z.object({
	chave: chavePerfilSchema,
	nome: nomeSchema,
	descricao: z.string().trim().max(200).nullable().default(null),
	escopo: escopoPerfilSchema,
	permissoes: z.array(permissaoSchema).min(1, "Escolha pelo menos uma permissão")
});
const atualizarPerfilRequestSchema = z.object({
	nome: nomeSchema,
	descricao: z.string().trim().max(200).nullable(),
	permissoes: z.array(permissaoSchema).min(1, "Escolha pelo menos uma permissão")
}).partial().refine((corpo) => Object.keys(corpo).length > 0, "Nada para alterar");
const membroSchema = z.object({
	usuario: z.object({
		id: z.uuid(),
		nome: z.string(),
		email: z.email(),
		ativo: z.boolean()
	}),
	perfil: perfilRefSchema,
	criadoEm: z.iso.datetime()
});
const adicionarMembroRequestSchema = z.object({
	email: emailSchema,
	chavePerfil: chavePerfilSchema
});
const atualizarMembroRequestSchema = z.object({ chavePerfil: chavePerfilSchema });
//#endregion
//#region src/documentos.ts
/** Documento fiscal genérico (ADR 0009): qualquer modelo, direção e origem. */
const modeloDocumentoSchema = z.enum([
	"NFE_55",
	"NFCE_65",
	"CTE_57",
	"CTE_OS_67",
	"NFSE"
]);
/** Direção do ponto de vista da EMPRESA do grupo (não do emitente). */
const direcaoSchema = z.enum(["ENTRADA", "SAIDA"]);
const papelEmpresaSchema = z.enum([
	"EMITENTE",
	"DESTINATARIO",
	"TOMADOR",
	"TRANSPORTADOR",
	"AUTORIZADO"
]);
const origemDocumentoSchema = z.enum([
	"UPLOAD_XML",
	"UPLOAD_ZIP",
	"CONECTOR",
	"API",
	"MIGRACAO",
	"SEFAZ_DFE"
]);
const situacaoDocumentoSchema = z.enum([
	"AUTORIZADA",
	"CANCELADA",
	"DENEGADA",
	"DESCONHECIDA"
]);
/** Fluxo interno do documento na empresa (evolui nas etapas 2–3). */
const statusFluxoSchema = z.enum([
	"RECEBIDO",
	"CLASSIFICADO",
	"VALIDADO",
	"ESCRITURADO"
]);
const tributoSchema = z.enum([
	"ICMS",
	"ICMS_ST",
	"ICMS_ST_RET",
	"FCP",
	"FCP_ST",
	"DIFAL",
	"IPI",
	"II",
	"PIS",
	"COFINS",
	"IBS_UF",
	"IBS_MUN",
	"CBS",
	"IS",
	"ISS"
]);
const participanteSchema = z.object({
	documento: z.string().nullable(),
	nome: z.string().nullable(),
	inscricaoEstadual: z.string().nullable(),
	uf: ufSchema.nullable(),
	municipio: z.string().nullable(),
	crt: z.string().nullable()
});
/** Linha da lista: o documento visto por UMA empresa (documento × empresa). */
const documentoResumoSchema = z.object({
	id: z.uuid(),
	empresaId: z.uuid(),
	chave: z.string().nullable(),
	modelo: modeloDocumentoSchema,
	serie: z.string(),
	numero: z.string(),
	direcao: direcaoSchema,
	/** `tpNF` do emitente (0 entrada, 1 saída), como no DANFE (spec 0011). */
	tipoOperacaoEmitente: z.enum(["0", "1"]),
	papel: papelEmpresaSchema,
	emitidaEm: z.iso.datetime({ offset: true }),
	/** A outra parte: o emitente numa entrada, o destinatário numa saída. */
	contraparte: participanteSchema.pick({
		documento: true,
		nome: true,
		uf: true
	}),
	naturezaOperacao: z.string(),
	valorTotal: decimalSchema,
	situacao: situacaoDocumentoSchema,
	statusFluxo: statusFluxoSchema,
	origem: origemDocumentoSchema,
	assinada: z.boolean(),
	importadoEm: z.iso.datetime(),
	/** Etapa 2: andamento da classificação dos itens e conferências que acusaram algo. */
	statusClassificacao: statusClassificacaoSchema,
	conferenciasComErro: z.number().int().nonnegative(),
	conferenciasComAlerta: z.number().int().nonnegative(),
	/** Spec 0011: nula até todos os itens estarem classificados. */
	classificacao: classificacaoDaNotaSchema.nullable()
});
const tributoDocumentoSchema = z.object({
	tributo: tributoSchema,
	cst: z.string().nullable(),
	base: decimalSchema.nullable(),
	aliquota: decimalSchema.nullable(),
	/** `null` = grupo presente sem destaque (classifique pelo CST, nunca por valor zero). */
	valor: decimalSchema.nullable()
});
const itemDocumentoSchema = z.object({
	nItem: z.number().int().positive(),
	codigoProduto: z.string(),
	ean: z.string().nullable(),
	descricao: z.string(),
	ncm: z.string().nullable(),
	cest: z.string().nullable(),
	cfop: z.string(),
	unidade: z.string(),
	quantidade: decimalSchema,
	valorUnitario: decimalSchema,
	valorTotal: decimalSchema,
	desconto: decimalSchema.nullable(),
	tributos: z.array(tributoDocumentoSchema)
});
const pagamentoDocumentoSchema = z.object({
	tipo: z.enum(["PAGAMENTO", "DUPLICATA"]),
	forma: z.string().nullable(),
	numero: z.string().nullable(),
	vencimento: z.iso.date().nullable(),
	valor: decimalSchema
});
const eventoDocumentoSchema = z.object({
	tipoEvento: z.string(),
	/** XML do evento (procEventoNFe) ou resumo da distribuição DF-e (sem texto nem cStat). */
	origem: z.enum(["XML_EVENTO", "RESUMO_DFE"]),
	descricao: z.string(),
	sequencia: z.number().int(),
	ocorridoEm: z.iso.datetime({ offset: true }),
	protocolo: z.string().nullable(),
	texto: z.string().nullable()
});
const arquivoDocumentoSchema = z.object({
	id: z.uuid(),
	tipo: z.enum([
		"XML_DOCUMENTO",
		"XML_EVENTO",
		"XML_RESUMO",
		"PDF"
	]),
	nomeOriginal: z.string(),
	tamanho: z.number().int().nonnegative(),
	sha256: z.string(),
	recebidoEm: z.iso.datetime()
});
const documentoDetalheSchema = documentoResumoSchema.extend({
	finalidade: z.string(),
	ambiente: z.enum(["1", "2"]),
	versaoLayout: z.string(),
	emitente: participanteSchema,
	destinatario: participanteSchema.nullable(),
	totais: z.object({
		valorProdutos: decimalSchema,
		valorFrete: decimalSchema,
		valorSeguro: decimalSchema,
		valorDesconto: decimalSchema,
		valorOutros: decimalSchema,
		valorTotal: decimalSchema,
		tributos: z.array(tributoDocumentoSchema)
	}),
	autorizacao: z.object({
		protocolo: z.string(),
		codigoStatus: z.string(),
		recebidoEm: z.iso.datetime({ offset: true })
	}).nullable(),
	itens: z.array(itemDocumentoSchema),
	pagamentos: z.array(pagamentoDocumentoSchema),
	eventos: z.array(eventoDocumentoSchema),
	arquivos: z.array(arquivoDocumentoSchema),
	informacaoComplementar: z.string().nullable(),
	/** Classificação por item (na ordem de `itens`) — CFOP de entrada, grupo contábil, patrimônio. */
	classificacaoItens: z.array(classificacaoDoItemSchema),
	conferencias: z.array(resultadoConferenciaSchema)
});
/** `GET /api/v1/documentos` — filtros combináveis; empresas vazias = todas as permitidas. */
const listarDocumentosQuerySchema = z.object({
	empresaIds: empresaIdsQuerySchema,
	direcao: direcaoSchema.optional(),
	modelo: modeloDocumentoSchema.optional(),
	situacao: situacaoDocumentoSchema.optional(),
	statusClassificacao: statusClassificacaoSchema.optional(),
	classificacao: classificacaoNotaSchema.optional(),
	/** Período de emissão, datas civis inclusivas. */
	emitidaDe: z.iso.date().optional(),
	emitidaAte: z.iso.date().optional(),
	/** Busca por chave, número, nome ou CNPJ da contraparte. */
	q: z.string().trim().max(120).optional(),
	ordenarPor: z.enum([
		"emitidaEm",
		"valorTotal",
		"importadoEm"
	]).default("emitidaEm"),
	...paginacaoQuerySchema.shape
});
/** Spec 0008: teto de notas num ZIP de XMLs (o resto do filtro pede outro período). */
const LIMITE_LOTE_XML = 5e3;
/** Spec 0010: o que o ZIP do lote leva — só XMLs, só PDFs (DANFE) ou os dois. */
const formatoLoteSchema = z.enum([
	"XML",
	"PDF",
	"XML_PDF"
]);
/** Spec 0010: teto com PDF, menor porque cada DANFE é desenhado (cerca de 120 ms de CPU). */
const LIMITE_LOTE_PDF = 1e3;
/** `GET /api/v1/documentos/lote.zip` — os filtros da lista e o formato (padrão: só XMLs). */
const loteDocumentosQuerySchema = listarDocumentosQuerySchema.extend({ formato: formatoLoteSchema.default("XML") });
/** Indicadores do topo da lista/início para o mesmo filtro. */
const resumoDocumentosSchema = z.object({
	quantidade: z.number().int().nonnegative(),
	valorTotal: decimalSchema,
	porSituacao: z.record(situacaoDocumentoSchema, z.number().int().nonnegative()),
	porDirecao: z.record(direcaoSchema, z.number().int().nonnegative()),
	chavesPendentes: z.number().int().nonnegative()
});
const chaveParamSchema = z.object({ chave: chaveAcessoSchema });
//#endregion
//#region src/importacao.ts
/** Importação em lote (docs/arquitetura/importacao.md). */
const tipoLoteSchema = z.enum(["ARQUIVOS", "PLANILHA"]);
const statusLoteSchema = z.enum([
	"RECEBIDO",
	"PROCESSANDO",
	"CONCLUIDO",
	"CONCLUIDO_COM_ERROS",
	"FALHOU"
]);
const resultadoItemLoteSchema = z.enum([
	"IMPORTADO",
	"ATUALIZADO",
	"DUPLICADO",
	"SEM_EMPRESA",
	"NAO_SUPORTADO",
	"INVALIDO",
	"ERRO",
	"CHAVE_PENDENTE",
	"CHAVE_JA_IMPORTADA"
]);
/** Formato reconhecido de planilha (Strategy pelo cabeçalho; GENERICA = só extrai chaves). */
const formatoPlanilhaSchema = z.enum(["SEFAZ_NFE_DESTINADAS", "GENERICA"]);
const contadoresLoteSchema = z.record(resultadoItemLoteSchema, z.number().int().nonnegative());
const loteSchema = z.object({
	id: z.uuid(),
	tipo: tipoLoteSchema,
	status: statusLoteSchema,
	formatoPlanilha: formatoPlanilhaSchema.nullable(),
	/** Empresa escolhida na importação (obrigatória para relatórios sem destinatário). */
	empresaId: z.uuid().nullable(),
	totalItens: z.number().int().nonnegative(),
	processados: z.number().int().nonnegative(),
	contadores: contadoresLoteSchema,
	criadoPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}),
	criadoEm: z.iso.datetime(),
	concluidoEm: z.iso.datetime().nullable()
});
/** `GET /api/v1/importacoes` — histórico em tabela: do mais novo para o mais antigo (ou o inverso). */
const listarLotesQuerySchema = paginacaoQuerySchema.extend({ ordenarPor: z.enum(["criadoEm", "totalItens"]).default("criadoEm") });
const itemLoteSchema = z.object({
	id: z.uuid(),
	nomeArquivo: z.string(),
	chave: z.string().nullable(),
	resultado: resultadoItemLoteSchema,
	mensagem: z.string().nullable(),
	documentoId: z.uuid().nullable()
});
const listarItensLoteQuerySchema = z.object({
	resultado: resultadoItemLoteSchema.optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(100)
});
/**
* `POST /api/v1/importacoes` — multipart: campo `arquivos` (XML/ZIP) ou `planilha` (CSV/XLSX),
* e `empresaId` opcional (obrigatório para planilha). Responde 202 com o lote criado.
*/
const criarLoteCamposSchema = z.object({ empresaId: z.uuid().optional() });
/** Limites do upload — também conferidos no server (docs/arquitetura/seguranca-lgpd.md). */
const LIMITES_UPLOAD = {
	bytesPorXml: 2097152,
	bytesPorZip: 209715200,
	bytesPorPlanilha: 20971520,
	arquivosPorEnvio: 500,
	entradasPorZip: 5e3,
	bytesDescompactadosPorZip: 1073741824
};
const statusChavePendenteSchema = z.enum([
	"PENDENTE",
	"OBTIDA",
	"INVALIDA"
]);
/** Nota conhecida pela chave (planilha/relatório) e ainda sem XML. */
const chavePendenteSchema = z.object({
	id: z.uuid(),
	empresaId: z.uuid(),
	chave: z.string(),
	status: statusChavePendenteSchema,
	emitenteDocumento: z.string(),
	emitenteNome: z.string().nullable(),
	uf: z.string(),
	modelo: z.string().nullable(),
	serie: z.string(),
	numero: z.string(),
	/** Do relatório: data/hora local SEM fuso (o relatório não informa); do XML: com fuso. */
	emitidaEm: z.iso.datetime({
		local: true,
		offset: true
	}).nullable(),
	valorTotal: decimalSchema.nullable(),
	/** Situação informada pela SEFAZ no relatório, como veio ("Autorizada", "Cancelada"...). */
	situacaoSefaz: z.string().nullable(),
	documentoId: z.uuid().nullable(),
	criadaEm: z.iso.datetime()
});
const listarChavesPendentesQuerySchema = z.object({
	empresaIds: empresaIdsQuerySchema,
	status: statusChavePendenteSchema.default("PENDENTE"),
	q: z.string().trim().max(120).optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50)
});
//#endregion
//#region src/localidades.ts
/** Estados com o nome por extenso e o código IBGE (o `cUF` da NF-e). "EX" (exterior) fica de fora. */
const UFS = [
	{
		sigla: "AC",
		nome: "Acre",
		codigoIbge: "12"
	},
	{
		sigla: "AL",
		nome: "Alagoas",
		codigoIbge: "27"
	},
	{
		sigla: "AP",
		nome: "Amapá",
		codigoIbge: "16"
	},
	{
		sigla: "AM",
		nome: "Amazonas",
		codigoIbge: "13"
	},
	{
		sigla: "BA",
		nome: "Bahia",
		codigoIbge: "29"
	},
	{
		sigla: "CE",
		nome: "Ceará",
		codigoIbge: "23"
	},
	{
		sigla: "DF",
		nome: "Distrito Federal",
		codigoIbge: "53"
	},
	{
		sigla: "ES",
		nome: "Espírito Santo",
		codigoIbge: "32"
	},
	{
		sigla: "GO",
		nome: "Goiás",
		codigoIbge: "52"
	},
	{
		sigla: "MA",
		nome: "Maranhão",
		codigoIbge: "21"
	},
	{
		sigla: "MT",
		nome: "Mato Grosso",
		codigoIbge: "51"
	},
	{
		sigla: "MS",
		nome: "Mato Grosso do Sul",
		codigoIbge: "50"
	},
	{
		sigla: "MG",
		nome: "Minas Gerais",
		codigoIbge: "31"
	},
	{
		sigla: "PA",
		nome: "Pará",
		codigoIbge: "15"
	},
	{
		sigla: "PB",
		nome: "Paraíba",
		codigoIbge: "25"
	},
	{
		sigla: "PR",
		nome: "Paraná",
		codigoIbge: "41"
	},
	{
		sigla: "PE",
		nome: "Pernambuco",
		codigoIbge: "26"
	},
	{
		sigla: "PI",
		nome: "Piauí",
		codigoIbge: "22"
	},
	{
		sigla: "RJ",
		nome: "Rio de Janeiro",
		codigoIbge: "33"
	},
	{
		sigla: "RN",
		nome: "Rio Grande do Norte",
		codigoIbge: "24"
	},
	{
		sigla: "RS",
		nome: "Rio Grande do Sul",
		codigoIbge: "43"
	},
	{
		sigla: "RO",
		nome: "Rondônia",
		codigoIbge: "11"
	},
	{
		sigla: "RR",
		nome: "Roraima",
		codigoIbge: "14"
	},
	{
		sigla: "SC",
		nome: "Santa Catarina",
		codigoIbge: "42"
	},
	{
		sigla: "SP",
		nome: "São Paulo",
		codigoIbge: "35"
	},
	{
		sigla: "SE",
		nome: "Sergipe",
		codigoIbge: "28"
	},
	{
		sigla: "TO",
		nome: "Tocantins",
		codigoIbge: "17"
	}
];
/** `GET /api/v1/localidades/ufs/:uf/municipios` — base do IBGE embarcada no server (sem internet). */
const municipioSchema = z.object({
	/** Código IBGE de 7 dígitos (o `cMun` da NF-e). */
	codigo: z.string().regex(/^\d{7}$/),
	nome: z.string()
});
/** `GET /api/v1/localidades/cep/:cep` — endereço do CEP (provedor externo, pode estar desligado). */
const enderecoDoCepSchema = z.object({
	cep: z.string().regex(/^\d{8}$/),
	logradouro: z.string().nullable(),
	bairro: z.string().nullable(),
	codigoMunicipio: z.string().regex(/^\d{7}$/),
	municipio: z.string(),
	uf: z.string().length(2)
});
const cepParamSchema = z.object({ cep: z.string().regex(/^\d{5}-?\d{3}$/, "CEP com 8 dígitos") });
/**
* Busca de municípios (padrão de select pesquisável remoto): `q` sem acento e sem diferenciar
* maiúsculas, por início de palavra; `codigo` devolve só aquele (para exibir o valor já gravado).
*/
const listarMunicipiosQuerySchema = z.object({
	q: z.string().trim().max(60).optional(),
	codigo: z.string().regex(/^\d{7}$/).optional(),
	limit: z.coerce.number().int().min(1).max(100).default(20)
});
//#endregion
//#region src/regras-classificacao.ts
/**
* Regras de classificação por empresa (spec 0007): "quando o item tiver tal característica,
* classificar como tal destino". A primeira regra ligada que casar classifica o item que ainda não
* foi classificado à mão; a escolha do fiscal prevalece sempre.
*/
const MAX_VALORES = 20;
const lista = (item) => z.array(item).max(MAX_VALORES).default([]);
/** Todas as condições informadas precisam casar; dentro de cada uma, qualquer valor da lista. */
const condicoesRegraSchema = z.object({
	/** Começo do NCM, só dígitos (2 a 8): "27101259", "2207". */
	ncmPrefixos: lista(z.string().regex(/^\d{2,8}$/, "NCM: de 2 a 8 dígitos")),
	/** CFOP de saída do fornecedor. */
	cfopsFornecedor: lista(z.string().regex(/^[5-7]\d{3}$/, "CFOP de saída: 4 dígitos (5, 6 ou 7)")),
	cnpjsEmitente: lista(cnpjSchema),
	/** Texto na descrição do produto, sem diferenciar maiúsculas e acentos. */
	descricaoContem: lista(z.string().trim().min(2).max(60))
}).refine((c) => c.ncmPrefixos.length + c.cfopsFornecedor.length + c.cnpjsEmitente.length + c.descricaoContem.length > 0, "Informe pelo menos uma condição");
/** Corpo de criar/editar. */
const regraClassificacaoEntradaSchema = z.object({
	nome: z.string().trim().min(1).max(80),
	ativa: z.boolean().default(true),
	condicoes: condicoesRegraSchema,
	classificacao: classificacaoItemSchema,
	/** Obrigatório quando a classificação é PERSONALIZADA (spec 0009). */
	classificacaoPersonalizadaId: z.uuid().nullable().default(null),
	centroCustoId: z.uuid().nullable().default(null)
}).refine((r) => r.classificacao === "PERSONALIZADA" === (r.classificacaoPersonalizadaId !== null), "Classificação personalizada exige a classificação da empresa");
const regraClassificacaoSchema = z.object({
	id: z.uuid(),
	nome: z.string(),
	ativa: z.boolean(),
	ordem: z.number().int().positive(),
	condicoes: condicoesRegraSchema,
	classificacao: classificacaoItemSchema,
	personalizada: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable(),
	centroCusto: z.object({
		id: z.uuid(),
		codigo: z.string(),
		nome: z.string()
	}).nullable(),
	/** Itens da empresa classificados hoje por esta regra. */
	itens: z.number().int().nonnegative(),
	atualizadaEm: z.iso.datetime(),
	atualizadaPor: z.object({
		id: z.uuid(),
		nome: z.string()
	}).nullable()
});
const listaRegrasClassificacaoSchema = z.object({ items: z.array(regraClassificacaoSchema) });
const ordemRegrasSchema = z.object({ ids: z.array(z.uuid()).min(1).max(200) });
/** Prévia de uma regra em edição: o que ela classificaria entre os itens já importados. */
const previaRegraEntradaSchema = z.object({
	condicoes: condicoesRegraSchema,
	/** A regra que está sendo editada (fica fora da conta de "pegos por regra acima"). */
	regraId: z.uuid().optional()
});
const previaRegraSchema = z.object({
	/** Itens de entrada da empresa que casam com as condições. */
	itens: z.number().int().nonnegative(),
	/** Desses, quantos já foram classificados à mão (não mudam). */
	jaManuais: z.number().int().nonnegative(),
	/** Desses, quantos uma regra ligada que vem antes já classifica. */
	pegosPorRegraAcima: z.number().int().nonnegative(),
	exemplos: z.array(z.object({
		emitente: z.string().nullable(),
		descricao: z.string(),
		ncm: z.string().nullable(),
		cfop: z.string(),
		valor: decimalSchema
	})).max(3)
});
/** Modelos prontos: um atalho que abre o editor preenchido; vira uma regra comum. */
const MODELOS_REGRA_CLASSIFICACAO = [{
	id: "combustiveis",
	nome: "Combustíveis",
	descricao: "Gasolina, diesel, etanol, biodiesel e GNV (sem lubrificante, ARLA e GLP)",
	classificacao: "COMBUSTIVEL",
	condicoes: {
		ncmPrefixos: [
			"27101259",
			"27101921",
			"27102000",
			"220710",
			"220720",
			"38260000",
			"27112100"
		],
		cfopsFornecedor: [],
		cnpjsEmitente: [],
		descricaoContem: []
	}
}];
//#endregion
//#region src/saude.ts
/** `GET /api/v1/health` (liveness) e `GET /api/v1/ready` (readiness, checa o banco). */
const saudeSchema = z.object({
	status: z.enum(["ok", "degradado"]),
	versao: z.string(),
	uptimeSegundos: z.number(),
	dependencias: z.record(z.string(), z.enum(["up", "down"])).optional()
});
//#endregion
export { AVISOS_VENCIMENTO_DIAS, CABECALHO_CSV_CENTROS_CUSTO, CHAVES_PERFIS_SISTEMA, CONFIGURACAO_CAPTURA_PADRAO, CONTADORES_CAPTURA_ZERADOS, DIAS_SEMANA, EXTENSOES_CERTIFICADO, INTERVALO_MINIMO_CAPTURA_MINUTOS, LIMITES_UPLOAD, LIMITE_ARQUIVO_CERTIFICADO, LIMITE_CONSULTAS_CHAVE_HORA, LIMITE_LOTE_PDF, LIMITE_LOTE_XML, MAXIMO_HORARIOS_CAPTURA, MENSAGEM_RECUSA, MINUTOS_NO_DIA, MODELOS_REGRA_CLASSIFICACAO, OPCOES_POR_PAGINA, PARAMETROS_CLASSIFICACAO_PADRAO, PENDENCIAS_CADASTRO, PERFIS_SISTEMA, PERMISSOES_EMPRESA, PERMISSOES_PLATAFORMA, POR_PAGINA_PADRAO, PROBLEMA_TROCA_SENHA, ROTULOS_PERMISSOES, UFS, adicionarMembroRequestSchema, agendaCapturaSchema, agendaContinuaSchema, agendaHorariosSchema, agendaJanelaSchema, arquivoDocumentoSchema, atualizarCentroCustoRequestSchema, atualizarEmpresaRequestSchema, atualizarMembroRequestSchema, atualizarPerfilRequestSchema, atualizarUsuarioRequestSchema, buscarAgoraRespostaSchema, cartaoCnpjSchema, centroCustoResumoSchema, centroCustoSchema, cepParamSchema, certificadoResumoSchema, cfopEntradaPersonalizadaSchema, cfopEntradaSchema, chaveAcessoSchema, chaveParamSchema, chavePendenteSchema, chavePerfilSchema, classificacaoDaNotaSchema, classificacaoDoItemSchema, classificacaoItemSchema, classificacaoNotaSchema, classificacaoPersonalizadaEntradaSchema, classificacaoPersonalizadaSchema, classificarDocumentoRequestSchema, cnaeSchema, cnpjSchema, codigoConferenciaSchema, codigoExternoSchema, condicoesRegraSchema, configuracaoCapturaRespostaSchema, configuracaoCapturaSchema, consultaChaveRequestSchema, consultaChaveRespostaSchema, contadorSchema, contadoresCapturaSchema, contadoresLoteSchema, contextoAcessoSchema, criarCentroCustoRequestSchema, criarEmpresaRequestSchema, criarLoteCamposSchema, criarPerfilRequestSchema, criarUsuarioRequestSchema, crtDoRegime, crtSchema, cursorPageSchema, cursorQuerySchema, dataSchema, decimalSchema, definirCentroCustoRequestSchema, definirContadorRequestSchema, definirParametrosFiscaisRequestSchema, definirSenhaRequestSchema, diaSemanaSchema, diasDaAgenda, direcaoSchema, documentoDetalheSchema, documentoResumoSchema, ehPermissao, ehPermissaoDePlataforma, emailSchema, empresaDetalheSchema, empresaIdsQuerySchema, empresaResumoSchema, enderecoCartaoSchema, enderecoDoCepSchema, enderecoSchema, escopoPerfilSchema, estadoCapturaSchema, eventoAuditoriaSchema, eventoDocumentoSchema, execucaoCapturaSchema, formatoLoteSchema, formatoPlanilhaSchema, grupoContabilSchema, grupoPersonalizadaSchema, horarioDosMinutos, horariosDoDia, idSchema, importarCentrosCustoQuerySchema, inscricaoEstadualSchema, itemDocumentoSchema, itemLoteSchema, linhaImportacaoCentroCustoSchema, listaClassificacoesPersonalizadasSchema, listaRegrasClassificacaoSchema, listarAuditoriaQuerySchema, listarCentrosCustoQuerySchema, listarChavesPendentesQuerySchema, listarDocumentosQuerySchema, listarEmpresasQuerySchema, listarExecucoesCapturaQuerySchema, listarItensLoteQuerySchema, listarLotesQuerySchema, listarMunicipiosQuerySchema, listarPendenciasQuerySchema, listarUsuariosQuerySchema, loginRequestSchema, loteDocumentosQuerySchema, loteSchema, membroSchema, minutosDoHorario, modeloDocumentoSchema, municipioSchema, naturezaCentroCustoSchema, ordemRegrasSchema, origemCadastroSchema, origemDocumentoSchema, origemGrupoSchema, pagamentoDocumentoSchema, paginaCentrosCustoSchema, paginaExecucoesCapturaSchema, paginaNumeradaSchema, paginacaoQuerySchema, painelCapturaQuerySchema, painelCapturaSchema, papelEmpresaSchema, parametrosClassificacaoSchema, parametrosFiscaisSchema, participanteSchema, pendenciaCadastroSchema, pendenciaSchema, perfilSchema, permissaoEmpresaSchema, permissaoPlataformaSchema, permissaoSchema, personalizadaResumoSchema, planilhaFiscalQuerySchema, previaRegraEntradaSchema, previaRegraSchema, prioridadeSchema, problemDetailsSchema, recusaCertificadoSchema, regimeTributarioSchema, regimeVigenciaSchema, regraClassificacaoEntradaSchema, regraClassificacaoSchema, resultadoConferenciaSchema, resultadoConsultaChaveSchema, resultadoImportacaoCentrosCustoSchema, resultadoItemLoteSchema, resumoDocumentosSchema, resumoPendenciasSchema, saudeSchema, senhaSchema, sessaoSchema, severidadeSchema, situacaoCapturaSchema, situacaoDocumentoSchema, statusChavePendenteSchema, statusClassificacaoSchema, statusExecucaoCapturaSchema, statusFluxoSchema, statusLoteSchema, substituirCnaesRequestSchema, substituirCodigosExternosRequestSchema, substituirInscricoesRequestSchema, substituirRegimesRequestSchema, tipoExecucaoCapturaSchema, tipoLoteSchema, tipoPendenciaSchema, tributoDocumentoSchema, tributoSchema, trocarSenhaRequestSchema, ufSchema, usuarioAtualSchema, usuarioResumoSchema, vinculoResumoSchema };

//# sourceMappingURL=index.mjs.map